/**
 * Eric 2026-06-25 (iter125 — Session A10) — Conteneur de tous les modals de
 * VideoDetailPage (Share, ponctuation, q&r clear history, proc clear history,
 * delete video, KnowledgeDetail, ReportMarkdown, ImportKnowledge, History,
 * KeyboardShortcuts).
 *
 * Extrait de VideoDetailPage.js (-551 lignes). Composant "dumb" : tous les
 * états sont injectés en props par le parent.
 */
import React from "react";
import { useLang } from "../../contexts/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Check, CircleNotch, Clock, Copy, LinkSimple, ShareNetwork, Trash, Warning, X,
} from "@phosphor-icons/react";
import TranscriptHistoryModal from "./TranscriptHistoryModal";
import KeyboardShortcutsModal from "./KeyboardShortcutsModal";
import ReportMarkdownModal from "./ReportMarkdownModal";
import ImportKnowledgeModal from "../ImportKnowledgeModal";
import { KnowledgeDetailModal } from "../knowledge/KnowledgeDetailModal";
import { formatTime } from "../../lib/videoDetailFormat";

export const VideoDetailModals = (props) => {
  const { tr } = useLang();
  const {
    lang, api, video, videoId, isAdmin, navigate,
    // Share Modal
    showShareModal, setShowShareModal,
    shareLinks, shareCopied, shareLoading, shareExpiry, setShareExpiry,
    createShareLink, copyShareLink, revokeShareLink,
    // History modal
    showHistory, setShowHistory, revisions, revisionsLoading,
    restoring, handleRestoreRevision,
    // Keyboard shortcuts modal
    showShortcutsHelp, setShowShortcutsHelp,
    // Report markdown modal
    reportModal, setReportModal, downloadReportMarkdown, downloadReportPDF,
    // Punctuation modal
    punctuationProposal, setPunctuationProposal,
    punctuationApplying, setPunctuationApplying,
    rejectedPunctMarks, setRejectedPunctMarks,
    acceptedPunctMarks, setAcceptedPunctMarks,
    loadAllTranscripts, loadVideoData,
    // Q&R clear history confirm
    qaClearHistoryConfirm, setQaClearHistoryConfirm,
    qaTitleAi, handleQaClearHistoryConfirmed,
    // Detail modal KU
    detailModalKu, setDetailModalKu,
    // Procédures clear history confirm
    procClearHistoryConfirm, setProcClearHistoryConfirm,
    setProcTitleHistory,
    // Import knowledge modal
    importOpen, setImportOpen,
    // Delete video modal
    showDeleteConfirm, setShowDeleteConfirm,
    deleteConfirmText, setDeleteConfirmText,
    deleting, setDeleting,
    onVideoDeleted,
  } = props;
  return (
    <>
      {/* ── Share Modal ── */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowShareModal(false)} data-testid="share-modal-overlay">
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 dark:border-zinc-800">
              <div className="flex items-center gap-2">
                <ShareNetwork size={20} className="text-[#002FA7]" />
                <h3 className="font-semibold text-base font-['Outfit']">{tr("Partager la vidéo", "Share the video")}</h3>
              </div>
              <button onClick={() => setShowShareModal(false)} className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors" data-testid="share-modal-close">
                <X size={18} />
              </button>
            </div>

            {/* Create new link */}
            <div className="px-5 py-4 border-b border-zinc-100 dark:border-zinc-800">
              <p className="text-sm text-zinc-500 mb-3">
                {tr(
                  "Générez un lien temporaire, accessible sans compte.",
                  "Generate a temporary link, usable without an account.",
                )}
              </p>
              <div className="flex items-center gap-2">
                <select
                  value={shareExpiry}
                  onChange={(e) => setShareExpiry(Number(e.target.value))}
                  className="h-9 px-3 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                  data-testid="share-expiry-select"
                >
                  <option value={1}>{tr("1 heure", "1 hour")}</option>
                  <option value={24}>{tr("24 heures", "24 hours")}</option>
                  <option value={72}>{tr("3 jours", "3 days")}</option>
                  <option value={168}>{tr("7 jours", "7 days")}</option>
                </select>
                <button
                  onClick={createShareLink}
                  disabled={shareLoading}
                  className="flex-1 h-9 flex items-center justify-center gap-2 bg-[#002FA7] text-white rounded-lg text-sm font-medium hover:bg-[#002280] transition-colors disabled:opacity-50"
                  data-testid="create-share-link-btn"
                >
                  {shareLoading ? <CircleNotch size={16} className="animate-spin" /> : <LinkSimple size={16} />}
                  {tr("Générer un lien", "Generate a link")}
                </button>
              </div>
            </div>

            {/* Active links */}
            <div className="px-5 py-3 max-h-64 overflow-y-auto">
              <p className="text-xs text-zinc-400 uppercase tracking-wider font-semibold mb-2">
                {tr("Liens actifs", "Active links")} ({shareLinks.length})
              </p>
              {shareLinks.length === 0 ? (
                <p className="text-sm text-zinc-400 py-4 text-center">{tr("Aucun lien actif", "No active link")}</p>
              ) : (
                <div className="space-y-2">
                  {shareLinks.map((link) => {
                    const expiresAt = new Date(link.expires_at);
                    const hoursLeft = Math.max(0, Math.round((expiresAt - new Date()) / 3600000));
                    const shareUrl = `${window.location.origin}/share/${link.token}`;
                    return (
                      <div key={link.token} className="flex items-center gap-2 p-2.5 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-700/50" data-testid={`share-link-${link.token}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono text-zinc-600 dark:text-zinc-300 truncate">{shareUrl}</p>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-400">
                            <span><Clock size={10} className="inline mr-0.5" />{hoursLeft}h restantes</span>
                            <span>{link.view_count} vue{link.view_count !== 1 ? "s" : ""}</span>
                            <span>{link.download_count} DL</span>
                          </div>
                        </div>
                        <button
                          onClick={() => copyShareLink(link.token)}
                          className="p-1.5 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded-md transition-colors"
                          title="Copier"
                          data-testid={`copy-link-${link.token}`}
                        >
                          {shareCopied === link.token ? <Check size={14} className="text-teal-500" /> : <Copy size={14} className="text-zinc-400" />}
                        </button>
                        <button
                          onClick={() => revokeShareLink(link.token)}
                          className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
                          title="Revoquer"
                          data-testid={`revoke-link-${link.token}`}
                        >
                          <Trash size={14} className="text-red-400" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transcript modification history modal */}
      <TranscriptHistoryModal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        revisions={revisions}
        loading={revisionsLoading}
        isAdmin={isAdmin}
        restoringId={restoring}
        onRestore={handleRestoreRevision}
        lang={lang}
        api={api}
        videoId={videoId}
      />
      {/* Eric 2026-05-12 — Modale d'aide raccourcis clavier (touche `?`) */}
      <KeyboardShortcutsModal
        open={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
        lang={lang}
      />
      {/* Eric 2026-05-05 — Modal de validation ponctuation (remplace Fireflies) */}
      {/* Vincent bug #27/#107 (2026-05-08) — Modal Rapport markdown (Q&R / Scripts / Procédures) */}
      <ReportMarkdownModal
        report={reportModal}
        onClose={() => setReportModal(null)}
        onDownloadMarkdown={downloadReportMarkdown}
        onDownloadPdf={downloadReportPDF}
        // Tâche 1266 — Le rapport des procédures part en Word, via le même
        // endpoint que le bouton Word du panneau Procédures.
        onDownloadWord={() => window.open(
          `${api}/api/videos/${videoId}/knowledge-report.docx?kind=procedure&full=1`,
          "_blank", "noopener")}
        lang={lang}
      />
      {punctuationProposal && (() => {
        // Vincent bug #73 + #74 (2026-05-07/08) — diff au caractère + REJET PAR MARQUE.
        // Chaque marque ajoutée par l'IA peut être individuellement acceptée (✓ vert)
        // ou refusée (X bordeaux). Au moment de l'apply, on reconstruit le texte
        // final en omettant les marques refusées.
        const o = punctuationProposal.original_text || "";
        const p = punctuationProposal.punctuated_text || "";
        const parts = [];
        let i = 0, j = 0;
        let pendingInsert = "";
        let pendingKeep = "";
        while (j < p.length) {
          if (i < o.length && p[j] === o[i]) {
            if (pendingInsert) { parts.push({ type: "insert", text: pendingInsert }); pendingInsert = ""; }
            pendingKeep += p[j];
            i++; j++;
          } else {
            if (pendingKeep) { parts.push({ type: "keep", text: pendingKeep }); pendingKeep = ""; }
            pendingInsert += p[j];
            j++;
          }
        }
        if (pendingInsert) parts.push({ type: "insert", text: pendingInsert });
        if (pendingKeep) parts.push({ type: "keep", text: pendingKeep });
        // Index of "insert" parts only — used for per-mark accept/reject state.
        const insertIndices = parts.map((p, idx) => p.type === "insert" ? idx : null).filter(idx => idx !== null);
        const totalInserts = insertIndices.length;
        const rejectedCount = insertIndices.filter(idx => rejectedPunctMarks[idx]).length;
        const acceptedCount = totalInserts - rejectedCount;

        return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !punctuationApplying && setPunctuationProposal(null)}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="punctuation-modal">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  ✏️ {lang === "fr" ? "Validation de la ponctuation auto" : "Auto-punctuation validation"}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  <span data-testid="punct-counter">
                    {lang === "fr"
                      ? `${acceptedCount} acceptée(s) · ${rejectedCount} refusée(s) sur ${totalInserts}`
                      : `${acceptedCount} accepted · ${rejectedCount} rejected of ${totalInserts}`}
                  </span>
                  {" · "}
                  {punctuationProposal.segments_count} {lang === "fr" ? "segments" : "segments"}
                  {punctuationProposal.truncated && <span className="ml-2 text-amber-600">⚠ {lang === "fr" ? "Texte tronqué (limite GPT)" : "Text truncated (GPT limit)"}</span>}
                </p>
              </div>
              <button
                onClick={() => !punctuationApplying && setPunctuationProposal(null)}
                disabled={punctuationApplying}
                className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
                data-testid="punctuation-modal-close"
              >
                ✕
              </button>
            </div>
            {/* Vincent bug #73 (2026-05-07) — vue diff au lieu d'un side-by-side
                qui obligeait à comparer toute la phrase. Maintenant on voit le
                texte avec UNIQUEMENT les marques de ponctuation ajoutées
                surlignées en bordeau (proposé) — beaucoup plus rapide à valider. */}
            <div className="flex-1 overflow-hidden flex flex-col p-4">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {lang === "fr" ? "Aperçu (changements surlignés)" : "Preview (changes highlighted)"}
                </h4>
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 font-medium" data-testid="punct-legend-proposed">
                  <span className="inline-block w-2 h-2 rounded-full bg-rose-600"></span>
                  {lang === "fr" ? "Bordeaux = proposé" : "Bordeaux = proposed"}
                </span>
                <span className="text-[10px] text-zinc-400">→</span>
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300 font-medium" data-testid="punct-legend-accepted">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-600"></span>
                  {lang === "fr" ? "Bleu = accepté" : "Blue = accepted"}
                </span>
                <span className="text-[10px] text-zinc-400">→</span>
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-zinc-200 text-zinc-700 border border-zinc-300 font-medium" data-testid="punct-legend-rejected">
                  <span className="inline-block w-2 h-2 rounded-full bg-zinc-500"></span>
                  {lang === "fr" ? "Gris = refusé" : "Gray = rejected"}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 bg-muted/30 rounded-lg border border-border text-sm whitespace-pre-wrap leading-relaxed font-serif" data-testid="punctuation-diff-view">
                {parts.map((part, idx) => part.type === "keep" ? (
                  <span key={idx}>{part.text}</span>
                ) : (
                  // Vincent bug #74 (2026-05-08) — 3 états visuels par marque :
                  //   bordeaux (proposed, défaut) · bleu (accepted, ✓ cliqué) · gris barré (rejected, ✗ cliqué).
                  // 2 boutons d'action sur chaque pill : ✓ accepter explicitement / ✗ refuser.
                  (() => {
                    const isRejected = !!rejectedPunctMarks[idx];
                    const isAccepted = !!acceptedPunctMarks[idx];
                    let stateClass;
                    if (isRejected) {
                      stateClass = "bg-zinc-200 text-zinc-400 line-through border-zinc-300";
                    } else if (isAccepted) {
                      stateClass = "bg-blue-100 text-blue-900 border-blue-500 font-bold";
                    } else {
                      stateClass = "bg-rose-100 text-rose-900 border-rose-500 font-bold";
                    }
                    return (
                      <span
                        key={idx}
                        className={`inline-flex items-center gap-0.5 mx-px px-1 rounded-sm border-b-2 ${stateClass}`}
                        data-testid={`punct-insert-${idx}`}
                        data-state={isRejected ? "rejected" : isAccepted ? "accepted" : "proposed"}
                      >
                        <span title={isRejected
                          ? tr("Marque refusée", "Mark rejected")
                          : isAccepted
                            ? tr("Marque acceptée", "Mark accepted")
                            : tr("Marque proposée par IA", "Mark proposed by AI")}>{part.text}</span>
                        {/* Vincent #74 — bouton ✓ accepter (devient bleu) */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAcceptedPunctMarks((m) => ({ ...m, [idx]: !m[idx] }));
                            setRejectedPunctMarks((m) => ({ ...m, [idx]: false }));
                          }}
                          className={`ml-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold transition-colors ${
                            isAccepted ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300"
                          }`}
                          title={isAccepted
                            ? tr("Marque déjà acceptée — cliquer pour annuler", "Mark already accepted — click to undo")
                            : tr("Accepter cette marque (passe en bleu)", "Accept this mark (turns blue)")}
                          data-testid={`punct-accept-${idx}`}
                          aria-label={tr("Accepter", "Accept")}
                        >
                          ✓
                        </button>
                        {/* Vincent #74 — bouton ✗ refuser (devient gris barré) */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRejectedPunctMarks((m) => ({ ...m, [idx]: !m[idx] }));
                            setAcceptedPunctMarks((m) => ({ ...m, [idx]: false }));
                          }}
                          className={`ml-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold transition-colors ${
                            isRejected ? "bg-teal-500 hover:bg-teal-600 text-white" : "bg-rose-500 hover:bg-rose-600 text-white"
                          }`}
                          title={isRejected
                            ? tr("Marque refusée — cliquer pour annuler", "Mark rejected — click to undo")
                            : tr("Refuser cette marque", "Reject this mark")}
                          data-testid={`punct-toggle-${idx}`}
                          aria-label={isRejected ? tr("Réaccepter", "Accept again") : tr("Refuser", "Reject")}
                        >
                          {isRejected ? "↺" : "✕"}
                        </button>
                      </span>
                    );
                  })()
                ))}
              </div>
              <details className="mt-2">
                <summary className="text-[11px] text-zinc-500 cursor-pointer hover:text-zinc-700">
                  {lang === "fr" ? "Voir l'avant/après côte à côte (mode complet)" : "Show side-by-side before/after"}
                </summary>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <div className="flex flex-col min-h-0">
                    <h4 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{lang === "fr" ? "Avant" : "Before"}</h4>
                    <div className="max-h-48 overflow-y-auto p-2 bg-muted/30 rounded text-xs whitespace-pre-wrap font-mono">{punctuationProposal.original_text}</div>
                  </div>
                  <div className="flex flex-col min-h-0">
                    <h4 className="text-[10px] font-semibold uppercase tracking-wide text-purple-600 mb-1">{lang === "fr" ? "Après" : "After"}</h4>
                    <div className="max-h-48 overflow-y-auto p-2 bg-purple-50 rounded text-xs whitespace-pre-wrap">{punctuationProposal.punctuated_text}</div>
                  </div>
                </div>
              </details>
            </div>
            {/* Vincent bug #74 (2026-05-07) — bouton ✗ Refuser bordeau (proposé) /
                ✓ Accepter vert. Le bordeau signale "à valider", le vert "accepté". */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button
                onClick={() => setPunctuationProposal(null)}
                disabled={punctuationApplying}
                className="px-4 py-2 text-sm rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-800 border border-rose-300 transition-colors disabled:opacity-50 font-medium flex items-center gap-1.5"
                data-testid="punctuation-cancel-btn"
              >
                <span className="text-base">✗</span>
                {lang === "fr" ? "Refuser" : "Reject"}
              </button>
              <button
                onClick={async () => {
                  if (punctuationApplying) return;
                  setPunctuationApplying(true);
                  try {
                    // Vincent bug #74 (2026-05-08) — Reconstruit le texte final
                    // en omettant les "insert" parts dont l'utilisateur a cliqué le X.
                    const finalText = parts
                      .map((part, idx) => (part.type === "insert" && rejectedPunctMarks[idx]) ? "" : part.text)
                      .join("");
                    await axios.post(
                      `${api}/api/videos/${videoId}/apply-punctuation`,
                      { punctuated_text: finalText },
                      { withCredentials: true, timeout: 60000 }
                    );
                    toast.success(lang === "fr" ? "Ponctuation appliquée. Vous pouvez encore éditer manuellement." : "Punctuation applied. You can still edit manually.");
                    setPunctuationProposal(null);
                    setRejectedPunctMarks({});
                    setAcceptedPunctMarks({});
                    await Promise.all([loadAllTranscripts(), loadVideoData()]);
                  } catch (err) {
                    toast.error(lang === "fr" ? `Erreur : ${err.response?.data?.detail || err.message}` : `Error: ${err.response?.data?.detail || err.message}`);
                  }
                  setPunctuationApplying(false);
                }}
                disabled={punctuationApplying}
                className="px-4 py-2 text-sm rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors disabled:opacity-50 font-semibold flex items-center gap-1.5 shadow-sm"
                data-testid="punctuation-apply-btn"
              >
                <span className="text-base">✓</span>
                {punctuationApplying ? (lang === "fr" ? "Application..." : "Applying...") : (lang === "fr" ? "Accepter" : "Accept")}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Vincent #139 (Eric 2026-05-13) — Modal de confirmation propre pour
          l'effacement de l'historique des titres Q&R (remplace le confirm() natif). */}
      {qaClearHistoryConfirm && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setQaClearHistoryConfirm(null)}
          data-testid="qa-clear-history-confirm-overlay"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-zinc-900 mb-2">
              {lang === "fr" ? "Effacer l'historique des titres ?" : "Clear title history?"}
            </h3>
            <p className="text-sm text-zinc-600 mb-4">
              {lang === "fr"
                ? "Cette action supprimera définitivement tous les anciens titres archivés pour cette Q&R. Vous ne pourrez plus revenir en arrière. Le titre actuel reste inchangé."
                : "This permanently removes all archived titles for this Q&A. You won't be able to revert. The current title stays untouched."}
            </p>
            <p className="text-xs text-zinc-500 mb-5">
              <strong>{(qaClearHistoryConfirm?.title || "").slice(0, 80)}</strong>
              {qaTitleAi.history.length > 0 && (
                <> · {qaTitleAi.history.length} {lang === "fr" ? "entrée(s) à supprimer" : "entry(ies) to delete"}</>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setQaClearHistoryConfirm(null)}
                className="px-3 py-1.5 text-sm bg-zinc-100 text-zinc-700 rounded hover:bg-zinc-200"
                data-testid="qa-clear-history-cancel"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
              <button
                onClick={handleQaClearHistoryConfirmed}
                className="px-3 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 font-medium"
                data-testid="qa-clear-history-confirm"
              >
                🗑 {lang === "fr" ? "Oui, tout effacer" : "Yes, clear all"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vincent bug #75 (2026-05-08) — Modal détail Q&R "comme base de connaissance"
          Enhancement B621.b (Eric 2026-07-30) — Callbacks prev/next injectés
          par VideoDetailPage à partir des siblings (KUs du même type dans la
          même vidéo). Permet une revue « feuilleter » sans fermer le modal. */}
      {detailModalKu && (
        <KnowledgeDetailModal
          unit={detailModalKu}
          videoTitle={video?.title || ""}
          formatTimestamp={(s) => formatTime(s || 0)}
          onClose={() => setDetailModalKu(null)}
          /* Eric (2026-02-10 — Vague 1 P2) — Scripts éditables via la
             modale partagée : on ne force PLUS `isAdmin=false`. La modale
             détecte `unit.__isScriptDoc` en interne pour brancher les
             endpoints sur `/api/scripts/{id}` (title, v2/v3 turns, status).
             Commentaires + Delete restent masqués pour scripts (Vague 2). */
          isAdmin={isAdmin}
          onUpdated={(updated) => {
            // Scripts : `loadVideoData` ne refetch pas `sales_scripts`
            // (collection séparée). On invalide via un tick incrémental
            // consommé par `ScriptsPage embedded`.
            if (detailModalKu.__isScriptDoc) {
              props.onScriptUpdated?.();
            } else {
              loadVideoData();
            }
            void updated;
          }}
          onNavigatePrev={props.detailModalNavPrev}
          onNavigateNext={props.detailModalNavNext}
          navigationLabel={props.detailModalNavLabel}
        />
      )}

      {/* Vincent 20/05/2026 — Modal de confirmation propre pour "Effacer l'historique" sur procédures */}
      {procClearHistoryConfirm && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setProcClearHistoryConfirm(null)}
          data-testid="proc-clear-history-confirm-overlay"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-zinc-900 mb-2">
              {lang === "fr" ? "Effacer l'historique des titres ?" : "Clear title history?"}
            </h3>
            <p className="text-sm text-zinc-600 mb-4">
              {lang === "fr"
                ? "Cette action supprimera définitivement tous les anciens titres archivés pour cette procédure. Le titre actuel reste inchangé."
                : "This permanently removes all archived titles for this procedure. Current title stays untouched."}
            </p>
            {procClearHistoryConfirm.title && (
              <p className="text-xs text-zinc-500 mb-5"><strong>{procClearHistoryConfirm.title.slice(0, 80)}</strong></p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setProcClearHistoryConfirm(null)}
                className="px-3 py-1.5 text-sm bg-zinc-100 text-zinc-700 rounded hover:bg-zinc-200"
                data-testid="proc-clear-history-cancel"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
              <button
                onClick={async () => {
                  const procId = procClearHistoryConfirm.procId;
                  setProcClearHistoryConfirm(null);
                  try {
                    await axios.delete(`${api}/api/agent/knowledge-units/${procId}/title-history`, { withCredentials: true });
                    setProcTitleHistory([]);
                    toast.success(lang === "fr" ? "Historique effacé" : "History cleared");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || (lang === "fr" ? "Échec" : "Failed"));
                  }
                }}
                className="px-3 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 font-medium"
                data-testid="proc-clear-history-confirm"
              >
                🗑 {lang === "fr" ? "Oui, tout effacer" : "Yes, clear all"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Eric (2026-05-18) — Modal d'import FAQ lié à cette vidéo. video_id
          est passé au backend pour que les KU créées soient rattachées à la
          vidéo et apparaissent dans ses onglets comme des Q&R IA. */}
      <ImportKnowledgeModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        videoId={videoId}
        videoTitle={video?.title || ""}
        onImported={() => { loadVideoData(); }}
      />

      {/* Vincent #258 (2026-02-06) — Modal double confirmation suppression vidéo.
          Étape 1 = warning generic, Étape 2 = taper le titre pour confirmer.
          Action IRRÉVERSIBLE : tout est supprimé en cascade côté backend. */}
      {showDeleteConfirm > 0 && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => !deleting && setShowDeleteConfirm(0)}
          data-testid="delete-video-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-2">
              <Warning size={22} className="text-red-600" weight="fill" />
              <h2 className="text-lg font-semibold text-red-700">
                {lang === "fr" ? "Suppression définitive" : "Permanent deletion"}
              </h2>
            </div>
            {showDeleteConfirm === 1 ? (
              <>
                <p className="text-sm text-zinc-700 mb-3">
                  {lang === "fr"
                    ? <>Cette action est <strong>irréversible</strong>. La vidéo <strong>« {video?.title} »</strong> et TOUTES ses connaissances dérivées seront supprimées :</>
                    : <>This action is <strong>irreversible</strong>. The video <strong>&quot;{video?.title}&quot;</strong> and ALL its derived knowledge will be removed:</>}
                </p>
                <ul className="text-xs text-zinc-600 list-disc ml-5 mb-4 space-y-0.5">
                  <li>{tr("Vidéo + fichier source", "Video + source file")}</li>
                  <li>{tr("Transcriptions (Whisper, Fireflies, fusionnée)", "Transcripts (Whisper, Fireflies, merged)")}</li>
                  <li>{tr("Toutes les Q&R, scripts, procédures, concepts", "Every Q&A, script, procedure and concept")}</li>
                  <li>{tr("Tentatives de quiz et complétions", "Quiz attempts and completions")}</li>
                  <li>{tr("Embeddings vectoriels", "Vector embeddings")}</li>
                </ul>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowDeleteConfirm(0)}
                    className="px-4 py-2 text-sm border border-zinc-300 rounded-lg hover:bg-zinc-50"
                    data-testid="delete-video-cancel-1"
                  >
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </button>
                  <button
                    onClick={() => { setShowDeleteConfirm(2); setDeleteConfirmText(""); }}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                    data-testid="delete-video-continue"
                  >
                    {lang === "fr" ? "Je comprends, continuer" : "I understand, continue"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-700 mb-2">
                  {lang === "fr"
                    ? <>Pour confirmer, tape le titre exact de la vidéo :</>
                    : <>To confirm, type the exact video title:</>}
                </p>
                <p className="text-xs text-zinc-500 font-mono bg-zinc-100 p-2 rounded mb-2 break-words">{video?.title}</p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder={lang === "fr" ? "Tape le titre ici…" : "Type the title here…"}
                  data-testid="delete-video-confirm-input"
                  autoFocus
                />
                <div className="flex gap-2 justify-end mt-4">
                  <button
                    onClick={() => setShowDeleteConfirm(0)}
                    disabled={deleting}
                    className="px-4 py-2 text-sm border border-zinc-300 rounded-lg hover:bg-zinc-50"
                    data-testid="delete-video-cancel-2"
                  >
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </button>
                  <button
                    onClick={async () => {
                      if (deleteConfirmText.trim() !== (video?.title || "").trim()) {
                        toast.error(lang === "fr" ? "Le titre ne correspond pas" : "Title doesn't match");
                        return;
                      }
                      setDeleting(true);
                      try {
                        const r = await axios.delete(`${api}/api/videos/${videoId}`, { withCredentials: true });
                        toast.success(lang === "fr" ? `Vidéo et ${Object.values(r.data?.cascade || {}).reduce((a, b) => a + b, 0)} éléments supprimés` : "Video deleted");
                        setShowDeleteConfirm(0);
                        navigate("/dashboard/videos");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || tr("Erreur", "Error"));
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    disabled={deleting || deleteConfirmText.trim() !== (video?.title || "").trim()}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="delete-video-final-btn"
                  >
                    {deleting
                      ? (lang === "fr" ? "Suppression…" : "Deleting…")
                      : (lang === "fr" ? "Effacer définitivement" : "Delete permanently")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VideoDetailModals;
